import { Box, Stack, TextField, MenuItem, Typography } from '@mui/material';

import type { SurfaceDensity } from '@/types/database';
import { PreviewCard } from './PreviewCard';
import { TwoColumn } from './TwoColumn';
import type { SiteStylesScreenProps } from './types';

const DENSITY_OPTIONS: { value: SurfaceDensity; label: string; description: string }[] = [
  { value: 'compact', label: 'Compact', description: 'Tighter spacing between sections and content — fits more on screen.' },
  { value: 'comfortable', label: 'Comfortable', description: 'Balanced spacing that works well for most units.' },
  { value: 'spacious', label: 'Spacious', description: 'Generous breathing room — a more editorial, gallery feel.' },
];

// Scoped deliberately to spacing density only for now — the design-system
// review's broader "Surfaces" concept (card/image/nav style, decorative
// treatment) needs changes across many individual block components and is
// tracked as a separate follow-up, not folded in here.
export function SurfacesScreen({ draft, updateTheme }: SiteStylesScreenProps) {
  const { surfaces } = draft.theme_config;

  const setDensity = (density: SurfaceDensity) =>
    updateTheme({ ...draft.theme_config, surfaces: { ...surfaces, density } }, { immediate: true });

  return (
    <Stack spacing={2.5} sx={{ p: 2.5 }}>
      <Typography variant="overline" color="text.secondary" sx={{ fontWeight: 700, display: 'block' }}>
        Surfaces
      </Typography>

      <TwoColumn
        controls={
          <TextField
            select
            label="Spacing Density"
            size="small"
            value={surfaces.density}
            onChange={(event) => setDensity(event.target.value as SurfaceDensity)}
            helperText={DENSITY_OPTIONS.find((option) => option.value === surfaces.density)?.description}
            fullWidth
          >
            {DENSITY_OPTIONS.map((option) => (
              <MenuItem key={option.value} value={option.value}>
                {option.label}
              </MenuItem>
            ))}
          </TextField>
        }
        preview={
          <PreviewCard themeConfig={draft.theme_config}>
            <Stack spacing={2}>
              <Box sx={{ bgcolor: 'action.hover', borderRadius: 1, p: 2 }}>
                <Typography sx={{ fontWeight: 700 }}>Section</Typography>
                <Typography variant="body2" color="text.secondary">
                  Spacing between elements scales with density.
                </Typography>
              </Box>
              <Box sx={{ bgcolor: 'action.hover', borderRadius: 1, p: 2 }}>
                <Typography sx={{ fontWeight: 700 }}>Section</Typography>
                <Typography variant="body2" color="text.secondary">
                  This preview reflects the current selection.
                </Typography>
              </Box>
            </Stack>
          </PreviewCard>
        }
      />
    </Stack>
  );
}
